import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Flex,
  Grid,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  HStack,
  VStack,
  Divider,
  Input,
  InputGroup,
  InputRightElement,
  Switch,
  FormControl,
  FormLabel,
  Icon,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Link,
  Tooltip,
  useColorModeValue,
  Badge,
  Progress,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  IconButton
} from '@chakra-ui/react';
import {
  FiArrowUp,
  FiArrowDown,
  FiBarChart2,
  FiRotateCw,
  FiDollarSign,
  FiTrendingUp,
  FiTrendingDown,
  FiClock,
  FiExternalLink,
  FiInfo,
  FiSettings,
  FiCheck,
  FiShare2,
  FiStar,
  FiLink
} from 'react-icons/fi';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { useThemeColors } from '../utils/themeUtils';
import './TokenPage.css';

// Mock data for token info
const tokenInfo = {
  name: 'Desol Token',
  symbol: 'DESOL',
  price: 0.0045,
  priceChange24h: 8.1,
  marketCap: 20000000,
  volume24h: 750000,
  circulatingSupply: 4400000000,
  totalSupply: 10000000000,
  address: 'DESoLFRn3WsgeeSUwfMSCkSx38ujY5nF7Pnx5iBgBzmK',
  holders: 12580,
  launchDate: '2023-06-15',
  socials: [
    { name: 'Website', url: 'https://desol.ai' },
    { name: 'Twitter', url: 'https://twitter.com/desolai' },
    { name: 'Telegram', url: 'https://t.me/desolai' },
    { name: 'Discord', url: 'https://discord.gg/desolai' }
  ],
  description: 'Desol AI is a decentralized hedge fund platform powered by artificial intelligence to maximize trading opportunities across the Solana ecosystem.',
  liquidity: 500000,
  fdv: 45000000,
  ath: 0.012,
  atl: 0.0008
};

// Mock data for trade history
const tradeHistory = [
  { type: 'buy', amount: '250,000', price: '$0.00451', value: '$1,127.50', time: '2 mins ago', txHash: 'Gq89...YhN2' },
  { type: 'sell', amount: '50,000', price: '$0.00448', value: '$224.00', time: '5 mins ago', txHash: 'Jr5L...KqT9' },
  { type: 'buy', amount: '500,000', price: '$0.00446', value: '$2,230.00', time: '15 mins ago', txHash: 'Pn7X...ZoL3' },
  { type: 'buy', amount: '100,000', price: '$0.00445', value: '$445.00', time: '28 mins ago', txHash: 'Tq2P...BgV7' },
  { type: 'sell', amount: '300,000', price: '$0.00442', value: '$1,326.00', time: '45 mins ago', txHash: 'Hk9D...XcN4' },
  { type: 'buy', amount: '175,000', price: '$0.00440', value: '$770.00', time: '1 hour ago', txHash: 'Vm3F...RjK8' },
  { type: 'sell', amount: '125,000', price: '$0.00438', value: '$547.50', time: '1.5 hours ago', txHash: 'Cs6R...MpE1' }
];

const TokenPage = () => {
  const { connected } = useWallet();
  const [timeframe, setTimeframe] = useState('1D');
  const [tradeType, setTradeType] = useState('buy');
  const [amount, setAmount] = useState(1000);
  const [slippage, setSlippage] = useState(1.0);
  const [selectedDex, setSelectedDex] = useState('Jupiter');
  
  // Use the theme colors hook for consistent theming
  const colors = useThemeColors();
  
  // Calculate estimated cost/receive value
  const estimatedValue = tradeType === 'buy' 
    ? (amount * tokenInfo.price).toFixed(2) 
    : (amount * tokenInfo.price * (1 - slippage / 100)).toFixed(2);

  return (
    <Container maxW="container.xl" py={4}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Flex align="center">
            <Heading as="h1" size="xl" color={colors.textColor}>
              {tokenInfo.symbol} Token
            </Heading>
            <Badge ml={2} colorScheme="purple" fontSize="sm">Solana</Badge>
          </Flex>
          <Text color={colors.secondaryTextColor} mt={1}>{tokenInfo.name}</Text>
        </Box>
        <HStack>
          <Button leftIcon={<FiStar />} colorScheme="yellow" variant="outline" size="sm">
            Favorite
          </Button>
          <Button leftIcon={<FiShare2 />} colorScheme="brand" variant="outline" size="sm">
            Share
          </Button>
        </HStack>
      </Flex>
      
      {/* Main content grid */}
      <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={6}>
        {/* Left column: Chart and token stats */}
        <Box>
          {/* Price overview */}
          <Card mb={6} bg={colors.cardBg} borderColor={colors.borderColor} borderWidth="1px" boxShadow="sm">
            <CardBody>
              <HStack justify="space-between" mb={2}>
                <Box>
                  <Text fontSize="2xl" fontWeight="bold" color={colors.textColor}>
                    ${tokenInfo.price.toFixed(6)}
                  </Text>
                  <Flex align="center">
                    <Text
                      fontSize="md"
                      color={tokenInfo.priceChange24h >= 0 ? colors.positiveColor : colors.negativeColor}
                      fontWeight="medium"
                      mr={2}
                    >
                      <Icon 
                        as={tokenInfo.priceChange24h >= 0 ? FiArrowUp : FiArrowDown}
                        mr={1}
                      />
                      {tokenInfo.priceChange24h}%
                    </Text>
                    <Text fontSize="sm" color={colors.secondaryTextColor}>
                      (24h)
                    </Text>
                  </Flex>
                </Box>
                <ButtonGroup size="sm" isAttached>
                  {['15M', '1H', '4H', '1D', '1W', '1M'].map(tf => (
                    <Button
                      key={tf}
                      onClick={() => setTimeframe(tf)}
                      colorScheme={timeframe === tf ? 'brand' : 'gray'}
                      variant={timeframe === tf ? 'solid' : 'outline'}
                    >
                      {tf}
                    </Button>
                  ))}
                </ButtonGroup>
              </HStack>
              
              {/* TradingView Chart Placeholder */}
              <Box
                height="400px"
                bg={colors.chartBg}
                borderRadius="md"
                borderWidth="1px"
                borderColor={colors.borderColor}
                p={4}
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                position="relative"
              >
                {/* This would be replaced with an actual chart library */}
                <Icon as={FiBarChart2} boxSize="50px" color="gray.400" />
                <Text mt={4} color={colors.secondaryTextColor}>
                  TradingView chart would be integrated here
                </Text>
                <Box position="absolute" top={3} right={3}>
                  <Icon as={FiSettings} color="gray.400" />
                </Box>
              </Box>
            </CardBody>
          </Card>
          
          {/* Token Stats */}
          <Card mb={6} bg={colors.cardBg} borderColor={colors.borderColor} borderWidth="1px" boxShadow="sm">
            <CardHeader pb={0}>
              <Heading size="md" color={colors.textColor}>Token Information</Heading>
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
                <Stat>
                  <StatLabel fontSize="sm" color={colors.secondaryTextColor}>Market Cap</StatLabel>
                  <StatNumber fontSize="lg">${(tokenInfo.marketCap).toLocaleString()}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel fontSize="sm" color={colors.secondaryTextColor}>24h Volume</StatLabel>
                  <StatNumber fontSize="lg">${(tokenInfo.volume24h).toLocaleString()}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel fontSize="sm" color={colors.secondaryTextColor}>Liquidity</StatLabel>
                  <StatNumber fontSize="lg">${(tokenInfo.liquidity).toLocaleString()}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel fontSize="sm" color={colors.secondaryTextColor}>Circulating Supply</StatLabel>
                  <StatNumber fontSize="lg">{(tokenInfo.circulatingSupply).toLocaleString()}</StatNumber>
                  <Progress 
                    value={(tokenInfo.circulatingSupply / tokenInfo.totalSupply) * 100} 
                    size="xs" 
                    colorScheme="brand" 
                    mt={1}
                  />
                </Stat>
                <Stat>
                  <StatLabel fontSize="sm" color={colors.secondaryTextColor}>Total Supply</StatLabel>
                  <StatNumber fontSize="lg">{(tokenInfo.totalSupply).toLocaleString()}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel fontSize="sm" color={colors.secondaryTextColor}>Holders</StatLabel>
                  <StatNumber fontSize="lg">{(tokenInfo.holders).toLocaleString()}</StatNumber>
                </Stat>
              </SimpleGrid>
              
              <Divider my={4} />
              
              <Box>
                <Text fontWeight="medium" mb={2} color={colors.textColor}>Token Contract</Text>
                <Flex 
                  bg={colors.hoverBg}
                  p={2} 
                  borderRadius="md" 
                  align="center" 
                  justify="space-between"
                >
                  <Text fontSize="sm" fontFamily="mono">
                    {tokenInfo.address}
                  </Text>
                  <HStack>
                    <Tooltip label="Copy address">
                      <IconButton 
                        icon={<FiLink />} 
                        size="sm" 
                        variant="ghost" 
                        aria-label="Copy address" 
                      />
                    </Tooltip>
                    <Tooltip label="View on Solscan">
                      <IconButton 
                        as={Link}
                        href={`https://solscan.io/token/${tokenInfo.address}`}
                        isExternal
                        icon={<FiExternalLink />} 
                        size="sm" 
                        variant="ghost" 
                        aria-label="View on explorer" 
                      />
                    </Tooltip>
                  </HStack>
                </Flex>
              </Box>
              
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={4}>
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm" color={colors.secondaryTextColor}>
                    <Icon as={FiInfo} mr={1} /> Launch Date
                  </Text>
                  <Text>{tokenInfo.launchDate}</Text>
                </VStack>
                <HStack spacing={4}>
                  {tokenInfo.socials.map((social, idx) => (
                    <Link href={social.url} isExternal key={idx}>
                      <Button size="sm" variant="outline">{social.name}</Button>
                    </Link>
                  ))}
                </HStack>
              </SimpleGrid>
            </CardBody>
          </Card>
          
          {/* Recent trades */}
          <Card bg={colors.cardBg} borderColor={colors.borderColor} borderWidth="1px" boxShadow="sm">
            <CardHeader pb={0}>
              <Heading size="md" color={colors.textColor}>Recent Trades</Heading>
            </CardHeader>
            <CardBody>
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>Type</Th>
                      <Th isNumeric>Amount</Th>
                      <Th isNumeric>Price</Th>
                      <Th isNumeric>Total</Th>
                      <Th>Time</Th>
                      <Th>Transaction</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {tradeHistory.map((trade, idx) => (
                      <Tr key={idx}>
                        <Td color={trade.type === 'buy' ? colors.positiveColor : colors.negativeColor} fontWeight="medium">
                          {trade.type.toUpperCase()}
                        </Td>
                        <Td isNumeric>{trade.amount}</Td>
                        <Td isNumeric>{trade.price}</Td>
                        <Td isNumeric>{trade.value}</Td>
                        <Td>{trade.time}</Td>
                        <Td>
                          <Link href={`https://solscan.io/tx/${trade.txHash}`} isExternal color="brand.400">
                            {trade.txHash} <Icon as={FiExternalLink} mx="2px" />
                          </Link>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </CardBody>
          </Card>
        </Box>
        
        {/* Right column: Swap interface */}
        <Flex direction="column">
          {/* Swap Card */}
          <Card bg={colors.cardBg} borderColor={colors.borderColor} borderWidth="1px" boxShadow="sm" mb={6}>
            <CardHeader>
              <Heading size="md" color={colors.textColor}>Swap {tokenInfo.symbol}</Heading>
            </CardHeader>
            <CardBody>
              <Tabs colorScheme="brand" isFitted mb={4}>
                <TabList>
                  <Tab 
                    onClick={() => setTradeType('buy')}
                  >
                    <Icon as={FiArrowDown} mr={2} /> 
                    Buy
                  </Tab>
                  <Tab 
                    onClick={() => setTradeType('sell')}
                  >
                    <Icon as={FiArrowUp} mr={2} /> 
                    Sell
                  </Tab>
                </TabList>
              </Tabs>
              
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel fontSize="sm">
                    {tradeType === 'buy' ? 'Amount to Buy' : 'Amount to Sell'}
                  </FormLabel>
                  <NumberInput
                    value={amount}
                    onChange={(valueString) => setAmount(parseFloat(valueString))}
                    min={0}
                    max={1000000000}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Text fontSize="xs" color={colors.secondaryTextColor} mt={1} textAlign="right">
                    ≈ {estimatedValue} {tradeType === 'buy' ? 'SOL' : 'USDC'}
                  </Text>
                </FormControl>
                
                <FormControl>
                  <Flex justify="space-between" align="center">
                    <FormLabel fontSize="sm" mb={0}>
                      DEX
                    </FormLabel>
                    <FormLabel fontSize="sm" mb={0}>
                      <Tooltip label="Auto-select best price from all DEXes">
                        <Flex align="center">
                          <Icon as={FiInfo} mr={1} fontSize="xs" />
                          Auto-route
                        </Flex>
                      </Tooltip>
                    </FormLabel>
                  </Flex>
                  <Select
                    value={selectedDex}
                    onChange={(e) => setSelectedDex(e.target.value)}
                    mt={1}
                  >
                    <option value="Jupiter">Jupiter</option>
                    <option value="Raydium">Raydium</option>
                    <option value="Orca">Orca</option>
                    <option value="Meteora">Meteora</option>
                  </Select>
                </FormControl>
                
                <FormControl>
                  <Flex justify="space-between" align="center">
                    <FormLabel fontSize="sm" mb={0}>
                      Slippage
                    </FormLabel>
                    <Text fontSize="sm" fontWeight="medium">
                      {slippage}%
                    </Text>
                  </Flex>
                  <Slider
                    value={slippage}
                    onChange={(val) => setSlippage(val)}
                    min={0.1}
                    max={5}
                    step={0.1}
                    mt={2}
                    colorScheme="brand"
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>
                
                <Box bg={colors.statBg} p={3} borderRadius="md">
                  <Flex justify="space-between" mb={1}>
                    <Text fontSize="sm" color={colors.secondaryTextColor}>Price</Text>
                    <Text fontSize="sm">${tokenInfo.price.toFixed(6)}</Text>
                  </Flex>
                  <Flex justify="space-between" mb={1}>
                    <Text fontSize="sm" color={colors.secondaryTextColor}>Price Impact</Text>
                    <Text fontSize="sm" color={colors.positiveColor}>&lt;0.01%</Text>
                  </Flex>
                  <Flex justify="space-between">
                    <Text fontSize="sm" color={colors.secondaryTextColor}>
                      {tradeType === 'buy' ? 'You Receive' : 'You Get'}
                    </Text>
                    <Text fontSize="sm">
                      {tradeType === 'buy' 
                        ? `${amount} DESOL`
                        : `${estimatedValue} USDC`}
                    </Text>
                  </Flex>
                </Box>
                
                {connected ? (
                  <Button 
                    colorScheme={tradeType === 'buy' ? 'green' : 'red'} 
                    size="lg" 
                    width="100%"
                  >
                    {tradeType === 'buy' ? 'Buy DESOL' : 'Sell DESOL'}
                  </Button>
                ) : (
                  <WalletMultiButton className="wallet-button-full" />
                )}
              </VStack>
            </CardBody>
          </Card>
          
          {/* Token stats */}
          <Card bg={colors.cardBg} borderColor={colors.borderColor} borderWidth="1px" boxShadow="sm">
            <CardHeader>
              <Heading size="md" color={colors.textColor}>Market Metrics</Heading>
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={2} spacing={4}>
                <Box>
                  <Text fontSize="sm" color={colors.secondaryTextColor}>
                    24h High
                  </Text>
                  <Text fontWeight="medium">
                    ${(tokenInfo.price * 1.03).toFixed(6)}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color={colors.secondaryTextColor}>
                    24h Low
                  </Text>
                  <Text fontWeight="medium">
                    ${(tokenInfo.price * 0.97).toFixed(6)}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color={colors.secondaryTextColor}>
                    All-time High
                  </Text>
                  <Text fontWeight="medium">
                    ${tokenInfo.ath.toFixed(6)}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color={colors.secondaryTextColor}>
                    All-time Low
                  </Text>
                  <Text fontWeight="medium">
                    ${tokenInfo.atl.toFixed(6)}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color={colors.secondaryTextColor}>
                    Fully Diluted Value
                  </Text>
                  <Text fontWeight="medium">
                    ${(tokenInfo.fdv).toLocaleString()}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color={colors.secondaryTextColor}>
                    Market Cap Rank
                  </Text>
                  <Text fontWeight="medium">
                    #156
                  </Text>
                </Box>
              </SimpleGrid>
              
              <Divider my={4} />
              
              <Box>
                <Text fontWeight="medium" mb={2} color={colors.textColor}>About {tokenInfo.name}</Text>
                <Text fontSize="sm" color={colors.secondaryTextColor}>
                  {tokenInfo.description}
                </Text>
              </Box>
            </CardBody>
          </Card>
        </Flex>
      </Grid>
    </Container>
  );
};

export default TokenPage; 